import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  extractDisplayName,
  extractEmailAddress,
  formatAddressChip,
  isValidAddressChip,
  splitAddressList,
} from '@/lib/addressParsing';
import type { ChangeEvent } from 'react';
import type { Editor } from '@tiptap/core';
import { useEditor } from '@tiptap/react';
import { sendEmail, fetchEmail } from '@/api/emails';
import { createDraft, updateDraft, deleteDraft } from '@/api/drafts';
import { ApiError } from '@/api/core/client';
import { triggerAccountSync } from '@/api/sync';
import { toast } from 'sonner';
import type { Account, Contact, Signature, Email } from '@/types/email';
import type { ReplyContext } from '@/types/ui';
import { getErrorMessage } from '@/lib/error';
import {
  composeExtensions,
  QUOTED_REPLY_BLOCKQUOTE_STYLE,
  QUOTED_REPLY_HEADER_STYLE,
} from '@/features/compose/editor/tiptapExtensions';
import {
  expandSignatureBlocks,
  insertSignatureBlockIntoEditor,
  normalizeSignatureHtml,
  removeSignatureBlockFromEditor,
} from '@/features/compose/utils/signatureUtils';
import type { ComposeAttachment, RecipientFieldKey } from '@/types/compose';
import { getComposeAttachmentBlob } from '@/features/compose/utils/composeAttachmentBlob';
import { removeEmailsFromStore } from '@/stores/emailStateHelpers';
import { useFolderStore } from '@/stores/folderStore';
import { mailEvents } from '@/events/mailEvents';
import { useEmailStore } from '@/stores/emailStore';
import { usePendingSendStore, type PendingSendPayload } from '@/stores/pendingSendStore';

interface UseComposeStateOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  replyTo?: ReplyContext;
  draftEmail?: Email | null;
  accounts: Account[];
  accountId?: string;
  contacts: Contact[];
  signatures: Signature[];
  mode?: 'standalone' | 'inline';
  onSent?: () => void;
  onClose?: () => void;
}

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const expandQuotedContentBlocks = (html: string): string => {
  if (typeof document === 'undefined') {
    return html;
  }

  let result = html;

  const blockRegex = /<div[^>]*class="[^"]*quoted-content-block[^"]*"[^>]*data-quoted-html="([^"]*)"[^>]*data-reply-header="([^"]*)"[^>]*>[\s\S]*?<\/div>/gi;
  const blockRegexNoHeader = /<div[^>]*class="[^"]*quoted-content-block[^"]*"[^>]*data-quoted-html="([^"]*)"[^>]*>[\s\S]*?<\/div>/gi;

  result = result.replace(blockRegex, (match, encodedHtml, encodedHeader) => {
    try {
      const decodedHtml = decodeURIComponent(encodedHtml);
      const decodedHeader = decodeURIComponent(encodedHeader);
      const headerMarkup = decodedHeader ? `<p style="${QUOTED_REPLY_HEADER_STYLE}">${decodedHeader}</p>` : '';
      const quotedMarkup = decodedHtml
        ? `<blockquote style="${QUOTED_REPLY_BLOCKQUOTE_STYLE}">${decodedHtml}</blockquote>`
        : '';
      return `${headerMarkup}${quotedMarkup}`;
    } catch (error) {
      console.warn('[ComposeDialog] Failed to decode quoted content', error);
      return match;
    }
  });

  result = result.replace(blockRegexNoHeader, (match, encodedHtml) => {
    try {
      const decodedHtml = decodeURIComponent(encodedHtml);
      const quotedMarkup = decodedHtml
        ? `<blockquote style="${QUOTED_REPLY_BLOCKQUOTE_STYLE}">${decodedHtml}</blockquote>`
        : '';
      return quotedMarkup;
    } catch (error) {
      console.warn('[ComposeDialog] Failed to decode quoted content', error);
      return match;
    }
  });

  return result;
};

const REPLY_PREFIX_RE = /^(re|aw|sv|antw|vs|ref|odp|ynt)\s*:/i;
const FORWARD_PREFIX_RE = /^(fwd|fw|wg|doorst|rv|enc|pd|ynt|İlt)\s*:/i;

const addReplyPrefix = (subject: string): string => {
  if (!subject) {
    return 'Re: ';
  }
  const trimmed = subject.trim();
  if (REPLY_PREFIX_RE.test(trimmed)) {
    return trimmed;
  }
  return `Re: ${trimmed}`;
};

const addForwardPrefix = (subject: string): string => {
  if (!subject) {
    return 'Fwd: ';
  }
  const trimmed = subject.trim();
  if (FORWARD_PREFIX_RE.test(trimmed)) {
    return trimmed;
  }
  return `Fwd: ${trimmed}`;
};

const stringifyAddressList = (addresses?: Array<{ email: string; name?: string | null }>): string => {
  if (!addresses || !addresses.length) {
    return '';
  }
  // formatAddressChip RFC-quotes names containing separators — a bare
  // `Name <email>` serialization re-split "Doe, John" into two chips (G10).
  return addresses
    .map((entry) => {
      if (!entry?.email) {
        return null;
      }
      return entry.name && entry.name !== entry.email
        ? formatAddressChip(entry.name, entry.email)
        : entry.email;
    })
    .filter(Boolean)
    .join(', ');
};

/**
 * Server attachments → composer attachments, stamped with the message that owns
 * them so they can be fetched again (preview, cross-account draft move).
 */
const mapEmailAttachmentsToCompose = (
  emailAttachments: Email['attachments'] | undefined,
  owner: { emailId: string | null | undefined; accountId: string | null | undefined },
): ComposeAttachment[] => {
  if (!emailAttachments || !emailAttachments.length) {
    return [];
  }
  return emailAttachments.map((attachment) => ({
    id: attachment.id,
    name: attachment.filename,
    size: attachment.size,
    mimeType: attachment.mimeType ?? undefined,
    status: 'uploaded' as const,
    error: null,
    remoteId: attachment.id,
    sourceEmailId: owner.emailId ?? undefined,
    sourceAccountId: owner.accountId ?? undefined,
  }));
};

const attachmentSnapshot = (attachments: ComposeAttachment[]): string =>
  attachments
    .map((attachment) =>
      attachment.remoteId
        ? `server:${attachment.remoteId}`
        : `local:${attachment.id}:${attachment.status}`
    )
    .sort()
    .join('|');

const plainTextFromHtml = (html: string): string =>
  html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Cue words that suggest the sender intended to attach a file. Matched against
// editor.getText(), which excludes the quoted original and signature (both are
// atom nodes expanded only at send time), so quoted "see attached" can't trip it.
const ATTACHMENT_CUE_RE =
  /\b(attach(?:ed|ment|ments|ing)?|enclos(?:ed|ing|ure))\b/i;

const mentionsAttachment = (text: string): boolean => ATTACHMENT_CUE_RE.test(text);

// Shared, quote-aware address utilities (one parser for all compose surfaces)
const extractEmailFromChip = extractEmailAddress;

const buildDraftSnapshot = (input: {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  html: string;
  attachments: ComposeAttachment[];
  fromAccountId: string | null;
}) =>
  JSON.stringify({
    to: input.to,
    cc: input.cc,
    bcc: input.bcc,
    subject: input.subject,
    html: input.html,
    attachments: attachmentSnapshot(input.attachments),
    fromAccountId: input.fromAccountId,
  });

/**
 * Parse a comma-separated address string ("Name <email>, ...") into the
 * EmailAddress array shape used by ThreadMessage.from / .to / .cc / .bcc.
 */
const parseAddressListForOptimistic = (raw: string | null | undefined) => {
  if (!raw) return [];
  return splitAddressList(raw).map((chip) => {
    const name = extractDisplayName(chip);
    const email = extractEmailAddress(chip);
    return { email, name: name ?? email };
  });
};

/**
 * Build a thread message we can optimistically append to the local thread view
 * the moment the user clicks Send. The actual SMTP send happens after the
 * undo-send window expires (~10s); without this, the thread view feels frozen.
 *
 * The message uses the idempotencyKey as its id so the realtime echo from the
 * server (when the real message arrives) can dedupe against it. Optimistic
 * messages are best-effort — if the send fails or is cancelled, the realtime
 * sync will reconcile.
 */
const buildOptimisticThreadMessage = (input: {
  threadId: string;
  accountId: string;
  from: { emailAddress: string; displayName?: string | null } | null;
  to: string;
  cc?: string | null;
  bcc?: string | null;
  subject: string;
  html: string;
  text: string;
  idempotencyKey: string;
}): import('@/types/email').ThreadMessageWithStatus | null => {
  if (!input.from?.emailAddress) {
    return null;
  }
  const fromAddress = {
    email: input.from.emailAddress,
    name: input.from.displayName || input.from.emailAddress,
  };
  return {
    id: `optimistic:${input.idempotencyKey}`,
    accountId: input.accountId,
    folderId: null,
    threadId: input.threadId,
    subject: input.subject,
    from: fromAddress,
    to: parseAddressListForOptimistic(input.to),
    cc: parseAddressListForOptimistic(input.cc),
    bcc: parseAddressListForOptimistic(input.bcc),
    date: new Date(),
    read: true,
    starred: false,
    body: input.text,
    bodyHtml: input.html,
    attachments: [],
    autoLabels: [],
    isArchived: false,
    isTrashed: false,
  } as import('@/types/email').ThreadMessageWithStatus;
};

export function useComposeState({
  open,
  onOpenChange,
  replyTo,
  draftEmail,
  accounts,
  accountId,
  contacts,
  signatures,
  mode = 'standalone',
  onSent,
  onClose,
}: UseComposeStateOptions) {
  const isInline = mode === 'inline';
  const [fromAccountId, setFromAccountId] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState<ComposeAttachment[]>([]);
  // Non-null while the "you mentioned an attachment" confirm is open; carries
  // the original send intent so confirm can resume the right path.
  const [attachmentNag, setAttachmentNag] = useState<
    { kind: 'send' } | { kind: 'schedule'; scheduledAt: Date } | null
  >(null);
  const attachmentNagBypassRef = useRef(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [draftLastSavedAt, setDraftLastSavedAt] = useState<number | null>(null);
  const [, setDraftInitialized] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [focusedField, setFocusedField] = useState<RecipientFieldKey | null>(null);
  const [activeInputValue, setActiveInputValue] = useState('');
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);
  const [appliedSignature, setAppliedSignature] = useState<{ id: string; html: string } | null>(null);
  const [signatureManuallySelected, setSignatureManuallySelected] = useState(false);
  const draftEmailId = draftEmail?.id ?? null;

  const fileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;
  const generateAttachmentId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const appendAttachments = useCallback((files: File[] | FileList) => {
    const incoming = Array.isArray(files) ? files : Array.from(files);
    if (!incoming.length) return;
    setAttachments((prev) => {
      const existingKeys = new Set(
        prev.filter((a) => a.file).map((a) => (a.file ? fileKey(a.file) : '')).filter(Boolean)
      );
      const next = incoming
        .filter((file) => file && file.size && !existingKeys.has(fileKey(file)))
        .map<ComposeAttachment>((file) => ({
          id: generateAttachmentId(),
          name: file.name,
          size: file.size,
          mimeType: file.type,
          file,
          status: 'pending',
          error: null,
          remoteId: undefined,
        }));
      return next.length ? [...prev, ...next] : prev;
    });
  }, []);

  const removeAttachmentFromStore = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetDraft = useCallback(() => {
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setShowCc(false);
    setShowBcc(false);
    setAttachments([]);
    setDraftId(null);
    setDraftSaving(false);
    setDraftDirty(false);
    setDraftError(null);
    setDraftLastSavedAt(null);
    setDraftInitialized(false);
    setSending(false);
    setFocusedField(null);
    setActiveInputValue('');
    setSelectedSignatureId(null);
    setSignatureManuallySelected(false);
    setAppliedSignature(null);
    initialSnapshotRef.current = null;
    draftIdRef.current = null;
  }, []);


  const attachmentsRef = useRef<ComposeAttachment[]>(attachments);
  const draftIdRef = useRef<string | null>(null);
  const editorHtmlRef = useRef<string>('');
  const handleSendRef = useRef<() => void>(() => {});
  const suppressEditorUpdateRef = useRef(false);
  const autosaveTimerRef = useRef<number | null>(null);
  const lastSnapshotRef = useRef<string | null>(null);
  const initialSnapshotRef = useRef<string | null>(null);
  const savingDraftRef = useRef(false);
  const draftErrorToastShownRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const creatingDraftRef = useRef(false);
  // In-flight autosave-create: send must AWAIT this so the draftId is known
  // and the sent draft gets cleaned up instead of orphaning a duplicate (G7).
  const draftCreatePromiseRef = useRef<Promise<Email | null> | null>(null);
  const latestDraftValuesRef = useRef({
    to,
    cc,
    bcc,
    subject,
    showCc,
    showBcc,
  });
  const lastDraftFolderIdRef = useRef<string | null>(draftEmail?.folderId ?? null);
  const activeThreadIdRef = useRef<string | null>(replyTo?.threadId ?? draftEmail?.threadId ?? null);
  // RFC 5322: Forwards are new conversations and should not carry threading headers
  const isForwardMode = replyTo?.mode === 'forward';
  const inReplyToRef = useRef<string | null>(isForwardMode ? null : (replyTo?.messageId ?? null));
  // RFC 5322: References = parent's References + parent's Message-ID
  const referencesRef = useRef<string | null>(
    isForwardMode ? null : (replyTo?.messageId
      ? [replyTo.references, replyTo.messageId].filter(Boolean).join(' ')
      : null)
  );
  const accountSwitchInFlightRef = useRef(false);
  // Idempotency key for the current compose session. Seeded from a carried key
  // on a resend-after-failure (so the server dedupes) and otherwise minted fresh
  // per send. Reset on every compose init so a stale key can't leak into an
  // unrelated compose (audit §2.9 #6).
  const idempotencyKeyRef = useRef<string | null>(replyTo?._expandState?.idempotencyKey ?? null);
  const [contentVersion, setContentVersion] = useState(0);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    draftIdRef.current = draftId;
  }, [draftId]);

  useEffect(() => {
    latestDraftValuesRef.current = {
      to,
      cc,
      bcc,
      subject,
      showCc,
      showBcc,
    };
  }, [to, cc, bcc, subject, showCc, showBcc]);

  useEffect(() => {
    activeThreadIdRef.current = replyTo?.threadId ?? draftEmail?.threadId ?? null;
  }, [draftEmail?.threadId, replyTo?.threadId]);

  // (No unconditional threading-header re-sync here: the composer is keyed by
  // (messageId, mode, draftId) at its mount sites, so re-targeting is a clean
  // REMOUNT — headers seed at ref init and in initialize*Draft (G4).)

  const hasDraftContent = useMemo(() => {
    const toValue = to.trim();
    const ccValue = (showCc ? cc : '').trim();
    const bccValue = (showBcc ? bcc : '').trim();
    const subjectValue = subject.trim();
    const htmlValue = editorHtmlRef.current || '';
    // reference contentVersion so memo reruns when editor updates
    const versionMarker = contentVersion;
    void versionMarker;
    const bodyPlain = plainTextFromHtml(htmlValue);
    const hasBody = bodyPlain.length > 0;
    const hasAttachments = attachments.length > 0;
    return Boolean(toValue || ccValue || bccValue || subjectValue || hasBody || hasAttachments);
  }, [attachments, bcc, cc, showBcc, showCc, subject, to, contentVersion]);

  const hasUserModified = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    // reference contentVersion so memo reruns when editor updates
    void contentVersion;
    const currentSnapshot = buildDraftSnapshot({
      to,
      cc: showCc ? cc : '',
      bcc: showBcc ? bcc : '',
      subject,
      html: editorHtmlRef.current,
      attachments,
      fromAccountId,
    });
    return currentSnapshot !== initialSnapshotRef.current;
  }, [to, cc, bcc, subject, showCc, showBcc, attachments, fromAccountId, contentVersion]);

  useEffect(() => {
    if (draftEmail?.folderId) {
      lastDraftFolderIdRef.current = draftEmail.folderId;
    }
  }, [draftEmail?.folderId]);

  const triggerDraftReload = useCallback(
    (folderId?: string | null, accountOverride?: string | null) => {
      const accountTarget = accountOverride ?? fromAccountId;
      if (!accountTarget) {
        return;
      }
      const targetFolderId = folderId ?? lastDraftFolderIdRef.current;
      if (!targetFolderId) {
        return;
      }
      mailEvents.emit('mail:mailbox:reload', {
        accountId: accountTarget,
        folderId: targetFolderId,
        options: { reset: false },
      });
    },
    [fromAccountId]
  );

  const scheduleDraftFolderCountRefresh = useCallback(
    (folderId?: string | null, accountOverride?: string | null) => {
      if (typeof window === 'undefined') {
        return;
      }
      const accountTarget = accountOverride ?? fromAccountId;
      if (!accountTarget) {
        return;
      }
      const targetFolderId = folderId ?? lastDraftFolderIdRef.current;
      if (!targetFolderId) {
        return;
      }
      const { scheduleReload } = useFolderStore.getState();
      scheduleReload(accountTarget, targetFolderId);
    },
    [fromAccountId]
  );

  const getLatestFieldValues = useCallback(() => {
    const snapshot = latestDraftValuesRef.current;
    return {
      toValue: snapshot.to,
      ccValue: snapshot.showCc ? snapshot.cc : '',
      bccValue: snapshot.showBcc ? snapshot.bcc : '',
      subjectValue: snapshot.subject,
    };
  }, []);

  const createFileLike = useCallback((blob: Blob, name: string, mimeType?: string): File => {
    if (typeof File !== 'undefined') {
      return new File([blob], name || 'attachment', {
        type: mimeType || blob.type || 'application/octet-stream',
        lastModified: Date.now(),
      });
    }
    const fallback = blob as File;
    return Object.assign(fallback, {
      name: name || 'attachment',
      lastModified: Date.now(),
    });
  }, []);

  const downloadAttachmentFile = useCallback(
    async (attachment: ComposeAttachment): Promise<File> => {
      const safeName = attachment.name || 'attachment';
      const blob = await getComposeAttachmentBlob(attachment);
      return createFileLike(blob, safeName, attachment.mimeType || blob.type);
    },
    [createFileLike]
  );

  const collectAttachmentFilesForTransfer = useCallback(async () => {
    const currentAttachments = attachmentsRef.current;
    const localFiles = currentAttachments
      .filter((attachment) => attachment.file && attachment.status !== 'error')
      .map((attachment) => attachment.file!)
      .filter(Boolean);
    const remoteAttachments = currentAttachments.filter(
      (attachment) => !attachment.file && attachment.remoteId && attachment.status === 'uploaded'
    );
    if (!remoteAttachments.length) {
      return localFiles;
    }
    const downloaded = await Promise.all(remoteAttachments.map((attachment) => downloadAttachmentFile(attachment)));
    return [...localFiles, ...downloaded];
  }, [downloadAttachmentFile]);


  const resolveSignatureForAccount = useCallback(
    (accountIdValue?: string | null) => {
      if (!accountIdValue) {
        return null;
      }
      const account = accounts.find((candidate) => candidate.id === accountIdValue);
      const defaultId = account?.defaultSignatureId ?? null;
      if (defaultId && signatures.some((signature) => signature.id === defaultId)) {
        return defaultId;
      }
      return null;
    },
    [accounts, signatures]
  );

  const insertInlineImageFromFile = useCallback(
    async (editorInstance: Editor | null, file: File, alt?: string) => {
      if (!editorInstance) {
        return;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        editorInstance.chain().focus().setImage({ src: dataUrl, alt: alt || file.name }).run();
      } catch (error) {
        console.error('Failed to insert image', error);
      }
    },
    []
  );

  const editor = useEditor({
    extensions: composeExtensions,
    content: '',
    onUpdate: ({ editor: editorInstance }) => {
      if (suppressEditorUpdateRef.current) {
        suppressEditorUpdateRef.current = false;
        return;
      }
      editorHtmlRef.current = editorInstance.getHTML();
      setContentVersion((value) => value + 1);
      setDraftDirty(true);
    },
    editorProps: {
      attributes: {
        class: isInline
          ? 'prose prose-sm max-w-none min-h-[110px] break-words text-sm leading-relaxed focus:outline-none'
          : 'prose prose-sm max-w-none min-h-[120px] h-full whitespace-pre-wrap p-3 leading-relaxed text-foreground focus:outline-none text-sm',
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault();
          handleSendRef.current();
          return true;
        }
        return false;
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (moved) {
          return false;
        }
        const transfer = event.dataTransfer;
        if (!transfer) {
          return false;
        }
        const files = Array.from(transfer.files ?? []).filter((file) => file && file.size);
        if (files.length === 0) {
          return false;
        }
        const images = files.filter((file) => file.type.startsWith('image/'));
        const others = files.filter((file) => !file.type.startsWith('image/'));
        if (images.length > 0) {
          event.preventDefault();
          images.forEach((file) => {
            void insertInlineImageFromFile(editor, file);
          });
        }
        if (others.length > 0) {
          event.preventDefault();
          appendAttachments(others);
        }
        return true;
      },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []).filter((file) => file && file.size);
        if (files.length === 0) {
          return false;
        }
        const images = files.filter((file) => file.type.startsWith('image/'));
        const others = files.filter((file) => !file.type.startsWith('image/'));
        if (images.length > 0) {
          event.preventDefault();
          images.forEach((file) => {
            void insertInlineImageFromFile(editor, file);
          });
        }
        if (others.length > 0) {
          event.preventDefault();
          appendAttachments(others);
        }
        return true;
      },
    },
  });

  const removeSignatureBlock = useCallback(() => removeSignatureBlockFromEditor(editor), [editor]);

  const removeQuotedContentBlock = useCallback(() => {
    if (!editor) {
      return false;
    }
    const ranges: Array<{ from: number; to: number }> = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'quotedContentBlock') {
        ranges.push({ from: pos, to: pos + node.nodeSize });
      }
    });
    if (!ranges.length) {
      return false;
    }
    editor.chain().focus().run();
    ranges
      .sort((a, b) => b.from - a.from)
      .forEach(({ from, to }) => {
        editor.commands.deleteRange({ from, to });
      });
    return true;
  }, [editor]);

  const insertQuotedContentBlock = useCallback(
    (html: string, replyHeader?: string, options?: { focusEditor?: boolean }) => {
      if (!editor) {
        return;
      }
      removeQuotedContentBlock();
      const { state, view } = editor;
      const quotedNode = state.schema.nodes.quotedContentBlock.create({
        html,
        replyHeader: replyHeader || '',
      });

      const endPos = state.doc.content.size;
      const transaction = state.tr.insert(endPos, quotedNode).scrollIntoView();
      view.dispatch(transaction);
      // Forward leaves the cursor in the To field, so the caller can suppress
      // the editor focus this insertion would otherwise steal.
      if (options?.focusEditor !== false) {
        editor.commands.focus('start');
      }
    },
    [editor, removeQuotedContentBlock]
  );

  const insertSignatureBlock = useCallback(
    (signature: Signature) => {
      if (!editor) {
        return;
      }
      const normalizedSignatureHtml = normalizeSignatureHtml(signature.html ?? '');
      const appliedHtml = insertSignatureBlockIntoEditor(editor, signature, {
        normalizedHtml: normalizedSignatureHtml,
      });
      if (appliedHtml) {
        setAppliedSignature({ id: signature.id, html: normalizedSignatureHtml });
      }
    },
    [editor, setAppliedSignature]
  );

  const handleSignatureSelect = useCallback(
    (value: string) => {
      setSignatureManuallySelected(true);
      if (value === 'none') {
        setSelectedSignatureId(null);
        const removed = removeSignatureBlock();
        if (removed) {
          setAppliedSignature(null);
        }
        return;
      }
      setSelectedSignatureId(value);
    },
    [removeSignatureBlock, setAppliedSignature, setSelectedSignatureId, setSignatureManuallySelected]
  );

  const applyAccountSignatureDefaults = useCallback(
    (accountIdValue: string | null) => {
      setSignatureManuallySelected(false);
      if (!accountIdValue) {
        setSelectedSignatureId(null);
        return;
      }
      const autoSignatureId = resolveSignatureForAccount(accountIdValue);
      if (autoSignatureId) {
        setSelectedSignatureId(autoSignatureId);
      } else {
        setSelectedSignatureId(null);
      }
    },
    [resolveSignatureForAccount, setSelectedSignatureId, setSignatureManuallySelected]
  );

  const moveDraftToAccount = useCallback(
    async (nextAccountId: string) => {
      if (!draftId || !fromAccountId || nextAccountId === fromAccountId) {
        if (nextAccountId !== fromAccountId) {
          setFromAccountId(nextAccountId);
        }
        applyAccountSignatureDefaults(nextAccountId);
        return;
      }
      if (accountSwitchInFlightRef.current) {
        return;
      }
      accountSwitchInFlightRef.current = true;
      const previousAccountId = fromAccountId;
      const previousDraftId = draftId;
      const previousFolderId = lastDraftFolderIdRef.current;
      setDraftSaving(true);
      setDraftError(null);
      try {
        const { toValue, ccValue, bccValue, subjectValue } = getLatestFieldValues();
        const htmlValue = editorHtmlRef.current;
        const textValue = editor?.getText() ?? htmlValue;
        const attachmentFiles = await collectAttachmentFilesForTransfer();
        const attachmentPayload = attachmentFiles.length ? attachmentFiles : undefined;
        const draft = await createDraft({
          accountId: nextAccountId,
          to: toValue,
          cc: ccValue,
          bcc: bccValue,
          subject: subjectValue,
          html: htmlValue,
          text: textValue,
          threadId: null,
          attachments: attachmentPayload,
        });
        draftIdRef.current = draft.id;
        setDraftId(draft.id);
        setFromAccountId(nextAccountId);
        applyAccountSignatureDefaults(nextAccountId);
        const mappedAttachments = mapEmailAttachmentsToCompose(draft.attachments, {
          emailId: draft.id,
          accountId: nextAccountId,
        });
        setAttachments(mappedAttachments);
        lastDraftFolderIdRef.current = draft.folderId ?? null;
        lastSnapshotRef.current = buildDraftSnapshot({
          to: toValue,
          cc: ccValue,
          bcc: bccValue,
          subject: subjectValue,
          html: htmlValue,
          attachments: mappedAttachments,
          fromAccountId: nextAccountId,
        });
        setDraftLastSavedAt(Date.now());
        setDraftDirty(false);
        setDraftInitialized(true);
        activeThreadIdRef.current = null;
        triggerDraftReload(draft.folderId ?? null, nextAccountId);
        scheduleDraftFolderCountRefresh(draft.folderId ?? null, nextAccountId);
        if (previousAccountId && previousDraftId) {
          try {
            await deleteDraft(previousAccountId, previousDraftId);
            removeEmailsFromStore([previousDraftId]);
            if (previousFolderId) {
              triggerDraftReload(previousFolderId, previousAccountId);
              scheduleDraftFolderCountRefresh(previousFolderId, previousAccountId);
            }
          } catch (deleteError) {
            console.warn('[Compose] Failed to delete previous draft during account change', deleteError);
            // The draft now exists in BOTH accounts — say so instead of leaving
            // a silent duplicate in the old account's Drafts (G15).
            toast.error('Draft moved, but the copy in the previous account could not be deleted');
          }
        }
      } catch (error) {
        setDraftError(getErrorMessage(error) ?? 'Failed to move draft to selected account');
      } finally {
        setDraftSaving(false);
        accountSwitchInFlightRef.current = false;
      }
    },
    [
      applyAccountSignatureDefaults,
      collectAttachmentFilesForTransfer,
      scheduleDraftFolderCountRefresh,
      draftId,
      fromAccountId,
      getLatestFieldValues,
      setAttachments,
      setDraftDirty,
      setDraftError,
      setDraftId,
      setDraftInitialized,
      setDraftLastSavedAt,
      setDraftSaving,
      setFromAccountId,
      triggerDraftReload,
      editor,
    ]
  );

  const handleAccountChange = useCallback(
    (value: string) => {
      if (!draftId || value === fromAccountId) {
        setFromAccountId(value);
        applyAccountSignatureDefaults(value);
        return;
      }
      void moveDraftToAccount(value);
    },
    [applyAccountSignatureDefaults, draftId, fromAccountId, moveDraftToAccount, setFromAccountId]
  );

  const initializeExistingDraft = useCallback(
    (email: Email) => {
      resetDraft();
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      setDraftId(email.id);
      setDraftSaving(false);
      setDraftDirty(false);
      setDraftError(null);
      activeThreadIdRef.current = email.threadId ?? null;
      const resolvedAccountId = email.accountId ?? accountId ?? accounts[0]?.id ?? null;
      setFromAccountId(resolvedAccountId);
      const toValue = stringifyAddressList(email.to);
      const ccValue = stringifyAddressList(email.cc);
      const bccValue = stringifyAddressList(email.bcc);
      setTo(toValue);
      setCc(ccValue);
      setBcc(bccValue);
      setShowCc(Boolean(ccValue));
      setShowBcc(Boolean(bccValue));
      setSubject(email.subject || '');
      setSignatureManuallySelected(true);
      setSelectedSignatureId(null);
      setAppliedSignature(null);
      setFocusedField(null);
      const htmlBody = email.bodyHtml ?? email.body ?? '';
      if (editor) {
        suppressEditorUpdateRef.current = true;
        editor.commands.setContent(htmlBody || '');
        removeSignatureBlock();
        removeQuotedContentBlock();
        editorHtmlRef.current = editor.getHTML();
      } else {
        editorHtmlRef.current = htmlBody;
      }
      const mappedAttachments = mapEmailAttachmentsToCompose(email.attachments, {
        emailId: email.id,
        accountId: resolvedAccountId,
      });
      setAttachments(mappedAttachments);
      const snapshot = buildDraftSnapshot({
        to: toValue,
        cc: ccValue,
        bcc: bccValue,
        subject: email.subject || '',
        html: editorHtmlRef.current,
        attachments: mappedAttachments,
        fromAccountId: resolvedAccountId,
      });
      lastSnapshotRef.current = snapshot;
      initialSnapshotRef.current = snapshot;
      setDraftLastSavedAt(Date.now());
      setDraftInitialized(true);
    },
    [
      accountId,
      accounts,
      editor,
      removeQuotedContentBlock,
      removeSignatureBlock,
      resetDraft,
      setAppliedSignature,
      setBcc,
      setCc,
      setDraftDirty,
      setDraftError,
      setDraftId,
      setDraftInitialized,
      setDraftLastSavedAt,
      setDraftSaving,
      setFocusedField,
      setFromAccountId,
      setSelectedSignatureId,
      setShowBcc,
      setShowCc,
      setSignatureManuallySelected,
      setSubject,
      setTo,
      setAttachments,
    ]
  );

  const initializeNewDraft = useCallback(() => {
    resetDraft();
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    setDraftId(null);
    setDraftSaving(false);
    setDraftDirty(false);
    setDraftError(null);
    setDraftLastSavedAt(null);
    setDraftInitialized(false);
    lastSnapshotRef.current = null;
    initialSnapshotRef.current = null;
    // Forward auto-focuses the To field (see focusEditorOnOpen below); reflect
    // that here so contact suggestions are armed immediately. Resetting to null
    // would clobber the focusedField set by the To input's autoFocus onFocus,
    // leaving suggestions dark until a manual blur/refocus.
    setFocusedField(replyTo?.mode === 'forward' ? 'to' : null);
    activeThreadIdRef.current = replyTo?.threadId ?? null;
    // Carry an in-flight key across a failed-send reopen; otherwise clear so a
    // fresh compose mints a new one (audit §2.9 #6).
    idempotencyKeyRef.current = replyTo?._expandState?.idempotencyKey ?? null;
    const isForwardReset = replyTo?.mode === 'forward';
    inReplyToRef.current = isForwardReset ? null : (replyTo?.messageId ?? null);
    referencesRef.current = isForwardReset ? null : (replyTo?.messageId
      ? [replyTo.references, replyTo.messageId].filter(Boolean).join(' ')
      : null);
    // From identity: inline→expand carried account → the reply's owning
    // account (G1) → host-provided account → first account.
    const initialAccount =
      replyTo?._expandState?.fromAccountId ??
      replyTo?.accountId ??
      accountId ??
      accounts[0]?.id ??
      null;
    setFromAccountId(initialAccount);
    // Undo-send / inline→expand carries the existing draft id: this session
    // must UPDATE that draft, not autosave-create a duplicate (G2).
    const carriedDraftId = replyTo?._expandState?.draftId ?? null;
    if (carriedDraftId) {
      draftIdRef.current = carriedDraftId;
      setDraftId(carriedDraftId);
    }
    setSignatureManuallySelected(false);
    setAppliedSignature(null);
    setSelectedSignatureId(null);

    // Capture baseline snapshot so we can detect user modifications vs auto-populated fields.
    // Must be called after all async editor setup (signature, quoted content) completes.
    // Uses refs so it reads the latest values at call time.
    const captureInitialState = () => {
      initialSnapshotRef.current = buildDraftSnapshot({
        to: latestDraftValuesRef.current.to,
        cc: latestDraftValuesRef.current.showCc ? latestDraftValuesRef.current.cc : '',
        bcc: latestDraftValuesRef.current.showBcc ? latestDraftValuesRef.current.bcc : '',
        subject: latestDraftValuesRef.current.subject,
        html: editorHtmlRef.current,
        attachments: attachmentsRef.current,
        fromAccountId: initialAccount,
      });
      lastSnapshotRef.current = initialSnapshotRef.current;
    };

    if (editor) {
      suppressEditorUpdateRef.current = true;
      editor.commands.setContent('');
      removeSignatureBlock();
      removeQuotedContentBlock();
      editorHtmlRef.current = '';
    } else {
      editorHtmlRef.current = '';
    }
    const autoSignatureId = resolveSignatureForAccount(initialAccount);
    if (autoSignatureId) {
      setSelectedSignatureId(autoSignatureId);
    }
    if (replyTo) {
      setTo(replyTo.to);
      // skipSubjectPrefix: a reopened (undone/failed) send carries its final
      // send-time subject — re-prefixing turned a fresh compose into "Re: …"
      // and re-added prefixes the user had deliberately stripped (N26).
      const prefixedSubject = replyTo.skipSubjectPrefix
        ? replyTo.subject
        : replyTo.mode === 'forward'
          ? addForwardPrefix(replyTo.subject)
          : addReplyPrefix(replyTo.subject);
      setSubject(prefixedSubject);
      if (replyTo.cc) {
        setCc(replyTo.cc);
        setShowCc(true);
      } else {
        setCc('');
        setShowCc(false);
      }
      setBcc('');
      setShowBcc(false);

      // Apply carried state from inline composer expansion
      if (replyTo._expandState) {
        const expand = replyTo._expandState;
        if (expand.attachments?.length) {
          setAttachments(expand.attachments);
        }
        if (expand.selectedSignatureId !== undefined) {
          setSelectedSignatureId(expand.selectedSignatureId);
          setSignatureManuallySelected(true);
        }
        if (expand.showBcc) {
          setShowBcc(true);
          setBcc(expand.bcc || '');
        }
      }

      // Forward: seed the original message's attachments as retained uploads so
      // the forwarded mail actually carries the files (audit §2.9 #5). Skipped
      // when inline-expand state already restored a (possibly edited) set.
      if (
        replyTo.mode === 'forward' &&
        !replyTo._expandState?.attachments?.length &&
        replyTo.attachments?.length
      ) {
        setAttachments(
          mapEmailAttachmentsToCompose(replyTo.attachments, {
            emailId: replyTo.sourceEmailId,
            accountId: replyTo.accountId,
          })
        );
      }

      // Forward opens with an empty To field (the user has to pick a new
      // recipient), so leave the cursor in that field — handled by the To
      // input's autoFocus — instead of stealing focus into the body. Reply and
      // reply-all pre-fill To, so they keep the cursor in the body as before.
      const focusEditorOnOpen = replyTo.mode !== 'forward';

      // If draftBody exists, use it as the main content (from inline composer expansion)
      if (replyTo.draftBody && editor) {
        requestAnimationFrame(() => {
          suppressEditorUpdateRef.current = true;
          editor.commands.setContent(replyTo.draftBody || '');
          editorHtmlRef.current = editor.getHTML();
          requestAnimationFrame(() => {
            if (focusEditorOnOpen) {
              editor.commands.focus('start');
            }
            captureInitialState();
          });
        });
      } else if (replyTo.body && editor) {
        // Otherwise, quote the original body as before
        requestAnimationFrame(() => {
          suppressEditorUpdateRef.current = true;
          editor.commands.setContent('');
          removeSignatureBlock();
          removeQuotedContentBlock();
          let replyHeader = '';
          const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
          if (replyTo.mode === 'forward') {
            const parts: string[] = [];
            parts.push('---------- Forwarded message ---------');
            if (replyTo.fromEmail) {
              parts.push(`From: ${replyTo.fromName || replyTo.fromEmail} &lt;${replyTo.fromEmail}&gt;`);
            }
            if (replyTo.date) {
              const date = new Date(replyTo.date);
              if (!Number.isNaN(date.getTime())) {
                const formattedDate = date.toLocaleString(locale, {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: '2-digit', hour12: true,
                });
                parts.push(`Date: ${formattedDate}`);
              }
            }
            if (replyTo.subject) {
              parts.push(`Subject: ${replyTo.subject}`);
            }
            if (replyTo.originalTo) {
              parts.push(`To: ${replyTo.originalTo}`);
            }
            replyHeader = parts.join('<br>');
          } else if (replyTo.date && replyTo.fromEmail) {
            const date = new Date(replyTo.date);
            const formattedDate = date.toLocaleString(locale, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });
            const senderName = replyTo.fromName || replyTo.fromEmail;
            replyHeader = `On ${formattedDate}, ${senderName} &lt;${replyTo.fromEmail}&gt; wrote:`;
          }

          insertQuotedContentBlock(replyTo.body || '', replyHeader, { focusEditor: focusEditorOnOpen });

          // Insert signature in the same synchronous block as quoted content
          // to avoid race conditions with the signature effect between rAFs.
          if (autoSignatureId) {
            const signature = signatures.find((sig) => sig.id === autoSignatureId);
            if (signature) {
              insertSignatureBlock(signature);
            }
          }

          requestAnimationFrame(() => {
            if (focusEditorOnOpen) {
              editor.commands.focus('start');
            }
            editorHtmlRef.current = editor.getHTML();
            captureInitialState();
          });
        });
      } else if (editor) {
        suppressEditorUpdateRef.current = true;
        editor.commands.setContent('');
        editorHtmlRef.current = '';

        // Insert signature even when there's no body to quote
        if (autoSignatureId) {
          const signature = signatures.find((sig) => sig.id === autoSignatureId);
          if (signature) {
            requestAnimationFrame(() => {
              insertSignatureBlock(signature);
              requestAnimationFrame(() => {
                if (focusEditorOnOpen) {
                  editor.commands.focus('start');
                }
                editorHtmlRef.current = editor.getHTML();
                captureInitialState();
              });
            });
          } else {
            requestAnimationFrame(() => captureInitialState());
          }
        } else {
          requestAnimationFrame(() => captureInitialState());
        }
      }
    } else {
      setTo('');
      setCc('');
      setBcc('');
      setShowCc(false);
      setShowBcc(false);
      setSubject('');
      if (editor) {
        suppressEditorUpdateRef.current = true;
        editor.commands.setContent('');
        editorHtmlRef.current = '';
      }
      requestAnimationFrame(() => captureInitialState());
    }
  }, [
    accountId,
    accounts,
    editor,
    insertQuotedContentBlock,
    insertSignatureBlock,
    removeQuotedContentBlock,
    removeSignatureBlock,
    replyTo,
    resolveSignatureForAccount,
    resetDraft,
    setAppliedSignature,
    setBcc,
    setCc,
    setDraftDirty,
    setDraftError,
    setDraftId,
    setDraftInitialized,
    setDraftLastSavedAt,
    setDraftSaving,
    setFocusedField,
    setFromAccountId,
    setSelectedSignatureId,
    setShowBcc,
    setShowCc,
    setSignatureManuallySelected,
    setSubject,
    setTo,
    signatures,
  ]);

  // The composer is keyed by session at its mount sites, so a re-target
  // remounts and this effect re-runs naturally. Within ONE session, re-init
  // only when the draft's content identity actually changes (partial→full
  // fetch). An unrelated dependency change (sidebar account switch refreshing
  // callback identities) must NOT re-run init — it would wipe in-progress
  // edits back to the stale prop and autosave them over the server copy (G5).
  const lastInitializedDraftRef = useRef<{ id: string; identity: string | null } | null>(null);
  useEffect(() => {
    if (!open) {
      lastInitializedDraftRef.current = null;
      sendingRef.current = false;
      setSending(false);
      return;
    }
    if (draftEmail) {
      // Identity covers recipients/subject/attachments too (N26): a partial→
      // full fetch that changed ONLY those never re-initialized when the
      // marker tracked bodyHtml alone.
      const identity = [
        draftEmail.bodyHtml ?? '',
        draftEmail.subject ?? '',
        (draftEmail.to ?? []).map((entry) => entry.email).join(','),
        (draftEmail.cc ?? []).map((entry) => entry.email).join(','),
        (draftEmail.attachments ?? []).map((attachment) => attachment.id).join(','),
      ].join(' ');
      const marker = { id: draftEmail.id, identity };
      const last = lastInitializedDraftRef.current;
      if (last && last.id === marker.id && last.identity === marker.identity) {
        return;
      }
      lastInitializedDraftRef.current = marker;
      initializeExistingDraft(draftEmail);
      return;
    }
    if (lastInitializedDraftRef.current?.id === '__new__') {
      return;
    }
    lastInitializedDraftRef.current = { id: '__new__', identity: null };
    initializeNewDraft();
  }, [open, draftEmail, draftEmailId, initializeExistingDraft, initializeNewDraft]);

  const createDraftFromState = useCallback(async (): Promise<Email | null> => {
    // Use draftIdRef to avoid race between React state update and ref clear
    const hasAttachmentsToUpload = attachmentsRef.current.some(a => a.file && !a.remoteId);
    if (!open || draftEmail || draftIdRef.current || !fromAccountId || (!hasUserModified && !hasAttachmentsToUpload) || sendingRef.current) {
      return null;
    }
    if (creatingDraftRef.current) {
      return null;
    }
    creatingDraftRef.current = true;
    setDraftSaving(true);
    setDraftError(null);
    try {
      const pendingUploads = attachmentsRef.current.filter((attachment) => attachment.file && !attachment.remoteId);
      const payloadAccountId = fromAccountId;
      if (!payloadAccountId) {
        return null;
      }
      const { toValue, ccValue, bccValue, subjectValue } = getLatestFieldValues();
      const htmlValue = editorHtmlRef.current;
      const textValue = editor?.getText() ?? htmlValue;
      const draft = await createDraft({
        accountId: payloadAccountId,
        to: toValue,
        cc: ccValue,
        bcc: bccValue,
        subject: subjectValue,
        html: htmlValue,
        text: textValue,
        threadId: activeThreadIdRef.current,
        inReplyTo: inReplyToRef.current,
        references: referencesRef.current,
        attachments: pendingUploads.map((attachment) => attachment.file!).filter(Boolean),
      });
      // Set ref immediately so concurrent callers see the ID before React re-renders
      draftIdRef.current = draft.id;
      setDraftId(draft.id);
      const mappedAttachments = mapEmailAttachmentsToCompose(draft.attachments, {
        emailId: draft.id,
        accountId: payloadAccountId,
      });
      setAttachments(mappedAttachments);
      lastDraftFolderIdRef.current = draft.folderId ?? lastDraftFolderIdRef.current;
      lastSnapshotRef.current = buildDraftSnapshot({
        to: toValue,
        cc: ccValue,
        bcc: bccValue,
        subject: subjectValue,
        html: htmlValue,
        attachments: mappedAttachments,
        fromAccountId: payloadAccountId,
      });
      setDraftLastSavedAt(Date.now());
      setDraftDirty(false);
      setDraftInitialized(true);
      triggerDraftReload(draft.folderId ?? null);
      scheduleDraftFolderCountRefresh(draft.folderId ?? null, payloadAccountId);
      return draft;
    } catch (error) {
      setDraftError(getErrorMessage(error) ?? 'Failed to save draft');
      return null;
    } finally {
      setDraftSaving(false);
      creatingDraftRef.current = false;
    }
  }, [
    draftEmail,
    editor,
    fromAccountId,
    getLatestFieldValues,
    hasUserModified,
    open,
    scheduleDraftFolderCountRefresh,
    setAttachments,
    setDraftDirty,
    setDraftError,
    setDraftId,
    setDraftInitialized,
    setDraftLastSavedAt,
    setDraftSaving,
    triggerDraftReload,
  ]);

  const ensureDraftId = useCallback(async (): Promise<string | null> => {
    // Check ref first to avoid race with React state batching
    if (draftIdRef.current) {
      return draftIdRef.current;
    }
    const createPromise = createDraftFromState();
    draftCreatePromiseRef.current = createPromise;
    try {
      const created = await createPromise;
      return created?.id ?? null;
    } finally {
      if (draftCreatePromiseRef.current === createPromise) {
        draftCreatePromiseRef.current = null;
      }
    }
  }, [createDraftFromState]);

  const persistDraft = useCallback(
    async (options: { force?: boolean } = {}): Promise<boolean> => {
      if (!open || !fromAccountId || sendingRef.current) {
        return true;
      }
      // Prevent concurrent saves — if a save is already in progress, mark a pending retry
      if (savingDraftRef.current) {
        if (options.force) {
          // A FORCED save ("Keep draft", unmount flush) must report the truth
          // (N26): returning true while the in-flight save could still fail
          // let the dialog close on unsaved edits. Wait it out, then save.
          while (savingDraftRef.current) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
          return persistDraft(options);
        }
        pendingSaveRef.current = true;
        return true;
      }
      // Set saving guard early to cover the ensureDraftId call
      savingDraftRef.current = true;
      let activeDraftId = draftIdRef.current;
      let justCreated = false;
      if (!activeDraftId) {
        if (!hasUserModified && !options.force) {
          savingDraftRef.current = false;
          return true;
        }
        activeDraftId = await ensureDraftId();
        if (!activeDraftId) {
          savingDraftRef.current = false;
          // Mark any 'uploading' attachments as failed since we couldn't create the draft
          setAttachments(prev => {
            if (!prev.some(a => a.status === 'uploading')) return prev;
            return prev.map(a =>
              a.status === 'uploading' ? { ...a, status: 'error' as const, error: 'Failed to save draft' } : a
            );
          });
          return false;
        }
        // ensureDraftId just created the draft with the latest state — skip the
        // redundant updateDraft call below unless new content arrived while creating.
        justCreated = true;
      }
      // Read EXCLUSIVELY from the live refs, never closure state: the 500ms
      // failure-retry below would otherwise re-save STALE recipients/subject
      // from the closure it was created in (G9).
      const latest = latestDraftValuesRef.current;
      const toValue = latest.to;
      const ccValue = latest.showCc ? latest.cc : '';
      const bccValue = latest.showBcc ? latest.bcc : '';
      const subjectValue = latest.subject;
      const htmlValue = editorHtmlRef.current;
      const textValue = editor?.getText() ?? htmlValue;
      const currentAttachments = attachmentsRef.current;
      const pendingUploads = currentAttachments.filter((attachment) => attachment.file && !attachment.remoteId);
      const retainedIds = currentAttachments
        .filter((attachment) => attachment.remoteId)
        .map((attachment) => attachment.remoteId!)
        .filter(Boolean);

      const snapshot = buildDraftSnapshot({
        to: toValue,
        cc: ccValue,
        bcc: bccValue,
        subject: subjectValue,
        html: htmlValue,
        attachments: currentAttachments,
        fromAccountId,
      });

      if (!options.force && lastSnapshotRef.current === snapshot && pendingUploads.length === 0) {
        savingDraftRef.current = false;
        return true;
      }

      // If we just created the draft and there are no new pending uploads,
      // skip the redundant updateDraft — the create already saved current state.
      if (justCreated && pendingUploads.length === 0) {
        savingDraftRef.current = false;
        return true;
      }

      setDraftSaving(true);
      setDraftError(null);
      try {
        const updated = await updateDraft(activeDraftId, {
          accountId: fromAccountId,
          to: toValue,
          cc: ccValue,
          bcc: bccValue,
          subject: subjectValue,
          html: htmlValue,
          text: textValue,
          threadId: activeThreadIdRef.current,
          inReplyTo: inReplyToRef.current,
          references: referencesRef.current,
          attachments: pendingUploads.map((attachment) => attachment.file!).filter(Boolean),
          retainedAttachmentIds: retainedIds,
        });
        const serverAttachments = mapEmailAttachmentsToCompose(updated.attachments, {
          emailId: updated.id,
          accountId: fromAccountId,
        });
        const pendingIds = new Set(pendingUploads.map((attachment) => attachment.id));
        const carry = attachmentsRef.current.filter(
          (attachment) => !pendingIds.has(attachment.id) && !attachment.remoteId
        );
        const nextAttachments = [...serverAttachments, ...carry];
        setAttachments(nextAttachments);
        lastDraftFolderIdRef.current = updated.folderId ?? lastDraftFolderIdRef.current;
        lastSnapshotRef.current = buildDraftSnapshot({
          to: toValue,
          cc: ccValue,
          bcc: bccValue,
          subject: subjectValue,
          html: editorHtmlRef.current,
          attachments: nextAttachments,
          fromAccountId,
        });
        setDraftDirty(false);
        setDraftLastSavedAt(Date.now());
        // A success ends the failure streak — the NEXT failure toasts again.
        // (Resetting this at the start of every attempt made each failed
        // autosave toast, piling up identical errors while the user typed.)
        draftErrorToastShownRef.current = false;
        triggerDraftReload(updated.folderId ?? null);
        return true;
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to save draft';
        // Surface the failure loudly ONCE per failure streak (the inline
        // composer has no draftError strip; a silent autosave failure loses
        // edits invisibly — G11).
        if (!draftErrorToastShownRef.current) {
          draftErrorToastShownRef.current = true;
          toast.error(message);
        }
        setDraftError(message);
        // Mark any 'uploading' attachments as failed
        setAttachments(prev => {
          if (!prev.some(a => a.status === 'uploading')) return prev;
          return prev.map(a =>
            a.status === 'uploading' ? { ...a, status: 'error' as const, error: 'Upload failed' } : a
          );
        });
        return false;
      } finally {
        savingDraftRef.current = false;
        setDraftSaving(false);
        // If content changed during save, re-save with latest values after a short delay
        // to avoid tight retry loops on persistent errors
        if (pendingSaveRef.current) {
          pendingSaveRef.current = false;
          setTimeout(() => {
            void persistDraft();
          }, 500);
        }
      }
    },
    [
      ensureDraftId,
      fromAccountId,
      hasUserModified,
      open,
      setAttachments,
      setDraftDirty,
      setDraftError,
      setDraftLastSavedAt,
      setDraftSaving,
      editor,
      triggerDraftReload,
    ]
  );

  useEffect(() => {
    if (!open || !fromAccountId) {
      return;
    }
    // For new drafts, wait until the user has actually modified content
    if (!draftEmail && !hasUserModified) {
      return;
    }
    // For existing drafts, wait until the user has modified from the loaded state
    if (draftEmail && !hasUserModified) {
      return;
    }
    const snapshot = buildDraftSnapshot({
      to,
      cc: showCc ? cc : '',
      bcc: showBcc ? bcc : '',
      subject,
      html: editorHtmlRef.current,
      attachments,
      fromAccountId,
    });
    if (lastSnapshotRef.current === snapshot) {
      return;
    }
    setDraftDirty(true);
    // 800ms debounce: long enough that autosave doesn't fire on every keystroke
    // for a fast typist, short enough that "type a sentence, alt-tab" reliably
    // captures the latest content. Increasing this risks lost edits when the
    // user closes/sends before the timer fires.
    const timer = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void persistDraft();
    }, 800);
    autosaveTimerRef.current = timer;
    return () => {
      clearTimeout(timer);
      if (autosaveTimerRef.current === timer) {
        autosaveTimerRef.current = null;
      }
    };
  }, [
    open,
    draftEmail,
    draftEmailId,
    fromAccountId,
    to,
    cc,
    bcc,
    subject,
    showCc,
    showBcc,
    attachments,
    contentVersion,
    hasUserModified,
    persistDraft,
    setDraftDirty,
  ]);

  // Auto-upload new attachments: when files are added, transition them to
  // 'uploading' and trigger an immediate draft persist so the user gets
  // visual feedback (spinner) and the files reach the server promptly.
  useEffect(() => {
    const hasPending = attachments.some(a => a.status === 'pending' && a.file);
    if (!hasPending || !open || !fromAccountId || sendingRef.current) return;

    setAttachments(prev => {
      const needsUpdate = prev.some(a => a.status === 'pending' && a.file);
      if (!needsUpdate) return prev;
      return prev.map(a =>
        a.status === 'pending' && a.file ? { ...a, status: 'uploading' as const } : a
      );
    });

    void persistDraft({ force: true });
  }, [attachments, open, fromAccountId, persistDraft, setAttachments]);

  const handleDiscardDraft = useCallback(async () => {
    if (!draftId) {
      resetDraft();
      setDraftId(null);
      setDraftInitialized(false);
      lastSnapshotRef.current = null;
      editorHtmlRef.current = '';
      return true;
    }
    if (!fromAccountId) {
      return false;
    }
    setDraftSaving(true);
    setDraftError(null);
    try {
      try {
        await deleteDraft(fromAccountId, draftId);
      } catch (error) {
        // 404 = the draft row is already gone server-side. Discard's goal is
        // met — clean up locally and close. Treating it as a failure trapped
        // the user in the close dialog with a Discard button that "did
        // nothing" whenever the draft had been deleted out from under them.
        if (!(error instanceof ApiError && error.status === 404)) {
          setDraftError(getErrorMessage(error) ?? 'Failed to delete draft');
          return false;
        }
      }
      removeEmailsFromStore([draftId]);
      resetDraft();
      setDraftId(null);
      setDraftInitialized(false);
      lastSnapshotRef.current = null;
      editorHtmlRef.current = '';
      triggerDraftReload();
      scheduleDraftFolderCountRefresh();
      lastDraftFolderIdRef.current = null;
      return true;
    } finally {
      setDraftSaving(false);
    }
  }, [
    draftId,
    fromAccountId,
    resetDraft,
    setDraftError,
    setDraftId,
    scheduleDraftFolderCountRefresh,
    setDraftInitialized,
    setDraftSaving,
    triggerDraftReload,
  ]);

  useEffect(() => {
    if (!fromAccountId) {
      setFromAccountId(accountId ?? accounts[0]?.id ?? null);
    }
  }, [accountId, accounts, fromAccountId, setFromAccountId]);


  useEffect(() => {
    if (!open || !fromAccountId) {
      return;
    }
    if (signatureManuallySelected) {
      console.debug('[ComposeDialog] Skipping auto signature (manual selection active)');
      return;
    }
    const autoSignatureId = resolveSignatureForAccount(fromAccountId);
    console.debug('[ComposeDialog] Evaluating auto signature', {
      fromAccountId,
      autoSignatureId,
      selectedSignatureId,
    });
    if (autoSignatureId) {
      if (selectedSignatureId !== autoSignatureId) {
        setSelectedSignatureId(autoSignatureId);
      }
    } else if (selectedSignatureId !== null) {
      setSelectedSignatureId(null);
    }
  }, [
    open,
    fromAccountId,
    signatureManuallySelected,
    selectedSignatureId,
    resolveSignatureForAccount,
    setSelectedSignatureId,
  ]);

  useEffect(() => {
    if (!selectedSignatureId) {
      return;
    }
    // Don't strip the applied signature if `signatures` is empty — the caller
    // may not have loaded them yet (or this is a transient render). Without
    // this guard, the inline reply composer can lose the user's signature
    // mid-typing if the parent briefly re-renders with a stale signatures prop.
    if (signatures.length === 0) {
      return;
    }
    if (!signatures.some((signature) => signature.id === selectedSignatureId)) {
      setSelectedSignatureId(null);
      setAppliedSignature(null);
      removeSignatureBlock();
      setSignatureManuallySelected(false);
    }
  }, [
    selectedSignatureId,
    signatures,
    removeSignatureBlock,
    setAppliedSignature,
    setSelectedSignatureId,
    setSignatureManuallySelected,
  ]);

  useEffect(() => {
    if (!editor || !open) {
      return;
    }
    if (!selectedSignatureId) {
      if (appliedSignature) {
        removeSignatureBlock();
        setAppliedSignature(null);
      }
      return;
    }
    const signature = signatures.find((item) => item.id === selectedSignatureId);
    if (!signature) {
      if (appliedSignature) {
        removeSignatureBlock();
        setAppliedSignature(null);
      }
      return;
    }
    const normalizedHtml = normalizeSignatureHtml(signature.html || '');
    if (appliedSignature && appliedSignature.id === signature.id && appliedSignature.html === normalizedHtml) {
      return;
    }
    insertSignatureBlock(signature);
  }, [
    editor,
    open,
    selectedSignatureId,
    signatures,
    appliedSignature,
    insertSignatureBlock,
    removeSignatureBlock,
    setAppliedSignature,
  ]);

  const suggestedContacts = useMemo(() => {
    if (!focusedField || !activeInputValue.trim()) {
      return [];
    }

    const normalized = activeInputValue.trim().toLowerCase();

    const exactMatch = contacts.some((contact) => contact.email.toLowerCase() === normalized);
    if (exactMatch) {
      return [];
    }

    return contacts
      .filter((contact) => {
        // Never suggest a blocked sender — picking one would email an address the
        // user has explicitly blocked.
        if (contact.approvedState === 'blocked') {
          return false;
        }
        const name = (contact.displayName || '').toLowerCase();
        const email = contact.email.toLowerCase();
        return name.includes(normalized) || email.includes(normalized);
      })
      .sort((a, b) => {
        // De-prioritize pending contacts so approved correspondents rank first.
        const pendingA = a.approvedState === 'pending' ? 1 : 0;
        const pendingB = b.approvedState === 'pending' ? 1 : 0;
        if (pendingA !== pendingB) {
          return pendingA - pendingB;
        }
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        if (scoreB !== scoreA) {
          return scoreB - scoreA;
        }
        const messagesA = a.totalMessages || 0;
        const messagesB = b.totalMessages || 0;
        if (messagesB !== messagesA) {
          return messagesB - messagesA;
        }
        const nameA = a.displayName || a.email;
        const nameB = b.displayName || b.email;
        return nameA.localeCompare(nameB);
      });
  }, [contacts, focusedField, activeInputValue]);

  const applyContactSuggestion = useCallback(
    (field: RecipientFieldKey, contact: Contact) => {
      const setter = field === 'to' ? setTo : field === 'cc' ? setCc : setBcc;
      const getter = field === 'to' ? to : field === 'cc' ? cc : bcc;
      const existing = splitAddressList(getter);
      // Avoid duplicates (compare by email part)
      const emailLower = contact.email.toLowerCase();
      if (!existing.some((e) => extractEmailFromChip(e).toLowerCase() === emailLower)) {
        existing.push(formatAddressChip(contact.displayName, contact.email));
      }
      setter(existing.join(', '));
      setActiveInputValue('');
    },
    [bcc, cc, setBcc, setCc, setTo, to]
  );

  /**
   * Merge a typed-but-uncommitted recipient into the focused field at send
   * time (the chip blur-commit is deferred 150ms, so "type address, hit Send"
   * silently dropped it — G6). Returns the effective to/cc/bcc, or null when
   * the pending text is not a valid address (send is blocked with a toast).
   */
  const mergePendingRecipientInput = useCallback((): { toValue: string; ccValue: string; bccValue: string } | null => {
    const latest = latestDraftValuesRef.current;
    let toValue = latest.to;
    let ccValue = latest.cc;
    let bccValue = latest.bcc;
    const pending = activeInputValue.trim();
    if (pending && focusedField) {
      if (!isValidAddressChip(pending)) {
        toast.error(`"${pending}" is not a valid email address`);
        return null;
      }
      const appendTo = (value: string) => {
        const chips = splitAddressList(value);
        const pendingEmail = extractEmailAddress(pending).toLowerCase();
        if (!chips.some((chip) => extractEmailAddress(chip).toLowerCase() === pendingEmail)) {
          chips.push(pending);
        }
        return chips.join(', ');
      };
      if (focusedField === 'to') {
        toValue = appendTo(toValue);
        setTo(toValue);
      } else if (focusedField === 'cc') {
        ccValue = appendTo(ccValue);
        setCc(ccValue);
      } else {
        bccValue = appendTo(bccValue);
        setBcc(bccValue);
      }
      setActiveInputValue('');
    }
    return { toValue, ccValue, bccValue };
  }, [activeInputValue, focusedField, setActiveInputValue, setBcc, setCc, setTo]);

  const handleSend = useCallback(async () => {
    if (sendingRef.current) return;

    const merged = mergePendingRecipientInput();
    if (!merged) {
      return;
    }
    const effectiveTo = merged.toValue;
    const effectiveCc = merged.ccValue;
    const effectiveBcc = merged.bccValue;

    if (!editor || !effectiveTo?.trim() || !subject?.trim()) {
      toast.error('Please fill in To and Subject fields');
      return;
    }

    if (!fromAccountId) {
      toast.error('Please select an account to send from');
      return;
    }

    const currentAtts = attachmentsRef.current;
    if (currentAtts.some(a => a.status === 'pending' || a.status === 'uploading')) {
      toast.error('Please wait for attachments to finish uploading');
      return;
    }
    if (currentAtts.some(a => a.status === 'error')) {
      toast.error('Some attachments failed to upload. Please remove them and try again.');
      return;
    }

    const bypassAttachmentNag = attachmentNagBypassRef.current;
    attachmentNagBypassRef.current = false;
    if (!bypassAttachmentNag && currentAtts.length === 0 && mentionsAttachment(editor.getText())) {
      setAttachmentNag({ kind: 'send' });
      return;
    }

    sendingRef.current = true;
    setSending(true);

    // An autosave-create may be mid-flight: await it so the draftId is known
    // and the server can clean the draft up after the send (otherwise the
    // create lands AFTER the send and orphans a duplicate draft — G7).
    if (draftCreatePromiseRef.current) {
      try {
        await draftCreatePromiseRef.current;
      } catch {
        // The create failed — nothing to clean up; proceed with the send.
      }
    }

    // Cancel any pending autosave to prevent orphaned draft creation after send
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    pendingSaveRef.current = false;

    // Serialize compose state before closing
    let html = editor.getHTML();
    html = expandSignatureBlocks(html);
    html = expandQuotedContentBlocks(html);
    const text = editor.getText();
    const currentAttachments = attachmentsRef.current;
    const attachmentFiles = currentAttachments
      .filter((a) => a.file)
      .map((a) => a.file!)
      .filter(Boolean);
    const retainedIds = currentAttachments
      .filter((a) => a.remoteId)
      .map((a) => a.remoteId!)
      .filter(Boolean);

    // Reuse a carried key (resend after a failed send) so the server dedupes;
    // otherwise mint a fresh one. Clear it after capture so the next distinct
    // send in this session gets a new key (audit §2.9 #6).
    const idempotencyKey = idempotencyKeyRef.current ?? crypto.randomUUID();
    idempotencyKeyRef.current = null;

    const sendPayload: PendingSendPayload = {
      accountId: fromAccountId,
      to: effectiveTo,
      cc: showCc && effectiveCc ? effectiveCc : undefined,
      bcc: showBcc && effectiveBcc ? effectiveBcc : undefined,
      subject,
      html,
      text,
      attachments: attachmentFiles.length ? attachmentFiles : undefined,
      // Carried on the payload (not just the executeSend closure) so the
      // pagehide keepalive flush sends them too — a flushed forward/draft
      // send otherwise arrived attachment-less (N7).
      retainedAttachmentIds: retainedIds.length ? retainedIds : undefined,
      threadId: activeThreadIdRef.current,
      inReplyTo: inReplyToRef.current,
      references: referencesRef.current,
      idempotencyKey,
      draftId: draftIdRef.current || draftId || null,
      showCc,
      showBcc,
      replyContext: replyTo || null,
      composeAttachments: [...currentAttachments],
      selectedSignatureId: selectedSignatureId,
    };

    // Close compose immediately — keep sendingRef true to block duplicate sends
    // from repeated keydown events before React re-renders
    setSending(false);
    resetDraft();
    setDraftId(null);
    setDraftInitialized(false);
    lastSnapshotRef.current = null;
    editor.commands.setContent('');
    editorHtmlRef.current = '';
    if (isInline) {
      onSent?.();
      onClose?.();
    } else {
      onOpenChange(false);
    }

    // Capture the thread context the user was viewing at send time so we can:
    //   1. Optimistically append the sent message to the thread immediately (for UI)
    //   2. Decide later whether to refresh the thread (only if they're still on it)
    const capturedDraftId = sendPayload.draftId;
    const sentTimeStore = useEmailStore.getState();
    const sentTimeViewedThreadId = sentTimeStore.selectedThreadId;
    const sentTimeReplyThreadId = sendPayload.threadId ?? null;

    // Optimistically add the sent message to the thread view if the user is on
    // the same thread they replied to. This avoids a 10s lag for the message
    // to appear in the thread (waiting for the actual SMTP send).
    if (sentTimeReplyThreadId && sentTimeViewedThreadId === sentTimeReplyThreadId) {
      const optimisticMessage = buildOptimisticThreadMessage({
        threadId: sentTimeReplyThreadId,
        accountId: sendPayload.accountId,
        from: accounts.find((a) => a.id === sendPayload.accountId) ?? null,
        to: sendPayload.to,
        cc: sendPayload.cc ?? null,
        bcc: sendPayload.bcc ?? null,
        subject: sendPayload.subject,
        html: sendPayload.html,
        text: sendPayload.text,
        idempotencyKey: sendPayload.idempotencyKey,
      });
      if (optimisticMessage) {
        useEmailStore.getState().appendOptimisticThreadMessage(optimisticMessage);
      }
    }

    // Enqueue with delayed execution
    usePendingSendStore.getState().enqueue(sendPayload, async (payload) => {
      // This runs after the undo window expires
      let returnedThreadId: string | null = null;

      const sendResult = await sendEmail({
        accountId: payload.accountId,
        to: payload.to,
        cc: payload.cc,
        bcc: payload.bcc,
        subject: payload.subject,
        html: payload.html,
        text: payload.text,
        attachments: payload.attachments,
        retainedAttachmentIds: retainedIds.length ? retainedIds : undefined,
        threadId: payload.threadId ?? undefined,
        inReplyTo: payload.inReplyTo ?? undefined,
        references: payload.references ?? undefined,
        idempotencyKey: payload.idempotencyKey,
        draftId: capturedDraftId ?? undefined,
      });
      returnedThreadId = sendResult.threadId;

      // If there was a draft, clean up local stores (server handles DB cleanup)
      if (capturedDraftId) {
        removeEmailsFromStore([capturedDraftId]);
        triggerDraftReload();
        scheduleDraftFolderCountRefresh();
      }

      // Refresh the thread so the sent message appears in the thread view —
      // but ONLY if the user is still viewing the same thread they sent on.
      // Otherwise the user has moved on, and we must not yank them back.
      const emailStore = useEmailStore.getState();
      const stillOnSameThread =
        sentTimeViewedThreadId !== null &&
        emailStore.selectedThreadId === sentTimeViewedThreadId;
      const viewedEmailId = emailStore.selectedEmailId;
      const viewedEmail = emailStore.selectedEmail;
      const viewedAccountId = viewedEmail?.accountId ?? null;
      const isCrossAccountReply = viewedAccountId && viewedAccountId !== payload.accountId;

      if (isCrossAccountReply && stillOnSameThread && viewedEmailId && viewedAccountId) {
        void triggerAccountSync(viewedAccountId).catch(() => {});
        const refreshViewedEmail = async (attempt: number) => {
          if (useEmailStore.getState().selectedEmailId !== viewedEmailId) return;
          try {
            const fresh = await fetchEmail(viewedAccountId, viewedEmailId);
            if (fresh.threadId) {
              const store = useEmailStore.getState();
              if (store.selectedEmailId === viewedEmailId) {
                void store.selectThread(String(fresh.threadId), viewedAccountId);
              }
              return;
            }
          } catch { /* ignore */ }
          if (attempt < 3) {
            setTimeout(() => refreshViewedEmail(attempt + 1), 2000);
          }
        };
        setTimeout(() => refreshViewedEmail(1), 2000);
      } else if (returnedThreadId && payload.accountId && stillOnSameThread) {
        // Same thread, same account — just refresh in place. Use the event so
        // the realtime handler can verify the user is still on the thread.
        setTimeout(() => {
          mailEvents.emit('mail:thread:refresh', {
            threadId: returnedThreadId,
            accountId: payload.accountId,
          });
        }, 500);
      }

      toast.success(isInline ? 'Reply sent!' : 'Email sent!');
    });
  }, [
    editor,
    subject,
    fromAccountId,
    showCc,
    showBcc,
    draftId,
    selectedSignatureId,
    replyTo,
    mergePendingRecipientInput,
    scheduleDraftFolderCountRefresh,
    onOpenChange,
    isInline,
    onSent,
    onClose,
    setDraftId,
    setDraftInitialized,
    triggerDraftReload,
    resetDraft,
    accounts,
  ]);

  const handleScheduleSend = useCallback(async (scheduledAt: Date) => {
    if (sendingRef.current) return;

    const merged = mergePendingRecipientInput();
    if (!merged) {
      return;
    }
    const effectiveTo = merged.toValue;
    const effectiveCc = merged.ccValue;
    const effectiveBcc = merged.bccValue;

    if (!editor || !effectiveTo?.trim() || !subject?.trim()) {
      toast.error('Please fill in To and Subject fields');
      return;
    }
    if (!fromAccountId) {
      toast.error('Please select an account to send from');
      return;
    }

    const scheduleAtts = attachmentsRef.current;
    if (scheduleAtts.some(a => a.status === 'pending' || a.status === 'uploading')) {
      toast.error('Please wait for attachments to finish uploading');
      return;
    }
    if (scheduleAtts.some(a => a.status === 'error')) {
      toast.error('Some attachments failed to upload. Please remove them and try again.');
      return;
    }

    const bypassAttachmentNag = attachmentNagBypassRef.current;
    attachmentNagBypassRef.current = false;
    if (!bypassAttachmentNag && scheduleAtts.length === 0 && mentionsAttachment(editor.getText())) {
      setAttachmentNag({ kind: 'schedule', scheduledAt });
      return;
    }

    sendingRef.current = true;
    setSending(true);

    // Await an in-flight autosave create for the same reason as handleSend (G7)
    if (draftCreatePromiseRef.current) {
      try {
        await draftCreatePromiseRef.current;
      } catch {
        // ignore — nothing to clean up
      }
    }

    // Cancel any pending autosave to prevent orphaned draft creation after send
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    pendingSaveRef.current = false;

    try {
      let html = editor.getHTML();
      html = expandSignatureBlocks(html);
      html = expandQuotedContentBlocks(html);
      const text = editor.getText();
      const attachmentFiles = attachmentsRef.current
        .filter((a) => a.file)
        .map((a) => a.file!)
        .filter(Boolean);
      const retainedIds = attachmentsRef.current
        .filter((a) => a.remoteId)
        .map((a) => a.remoteId!)
        .filter(Boolean);
      // Capture the draft id so the server can delete the source draft once the
      // scheduled send is committed to the outbox (otherwise scheduling leaves a
      // leftover draft behind).
      const capturedDraftId = draftIdRef.current || draftId || null;

      // ONE idempotency key for the whole schedule attempt-series: a client
      // timeout + user retry must dedupe server-side, not double-send (G16).
      // The key is cleared only after the request SUCCEEDS.
      const idempotencyKey = idempotencyKeyRef.current ?? crypto.randomUUID();
      idempotencyKeyRef.current = idempotencyKey;

      await sendEmail({
        accountId: fromAccountId,
        to: effectiveTo,
        cc: showCc && effectiveCc ? effectiveCc : undefined,
        bcc: showBcc && effectiveBcc ? effectiveBcc : undefined,
        subject,
        html,
        text,
        attachments: attachmentFiles.length ? attachmentFiles : undefined,
        retainedAttachmentIds: retainedIds.length ? retainedIds : undefined,
        threadId: activeThreadIdRef.current ?? undefined,
        inReplyTo: inReplyToRef.current ?? undefined,
        references: referencesRef.current ?? undefined,
        idempotencyKey,
        scheduledAt: scheduledAt.toISOString(),
        draftId: capturedDraftId ?? undefined,
      });
      idempotencyKeyRef.current = null;

      if (capturedDraftId) {
        removeEmailsFromStore([capturedDraftId]);
        triggerDraftReload();
        scheduleDraftFolderCountRefresh();
      }

      sendingRef.current = false;
      setSending(false);
      const timeStr = scheduledAt.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
      toast.success(`Email scheduled for ${timeStr}`);
      resetDraft();
      setDraftId(null);
      setDraftInitialized(false);
      lastSnapshotRef.current = null;
      editor.commands.setContent('');
      editorHtmlRef.current = '';
      if (isInline) {
        onSent?.();
        onClose?.();
      } else {
        onOpenChange(false);
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) ?? 'Failed to schedule email');
      sendingRef.current = false;
      setSending(false);
    }
  }, [
    editor, subject, fromAccountId, showCc, showBcc, draftId,
    mergePendingRecipientInput,
    onOpenChange, isInline, onSent, onClose, setDraftId, setDraftInitialized, resetDraft,
    triggerDraftReload, scheduleDraftFolderCountRefresh,
  ]);

  // Keep ref in sync so the editor's handleKeyDown can call the latest handleSend
  handleSendRef.current = handleSend;

  const dismissAttachmentNag = useCallback(() => {
    setAttachmentNag(null);
  }, []);

  const confirmSendWithoutAttachments = useCallback(() => {
    const nag = attachmentNag;
    setAttachmentNag(null);
    if (!nag) return;
    attachmentNagBypassRef.current = true;
    if (nag.kind === 'schedule') {
      void handleScheduleSend(nag.scheduledAt);
    } else {
      void handleSend();
    }
  }, [attachmentNag, handleSend, handleScheduleSend]);

  const handleFileSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        appendAttachments(files);
        setDraftDirty(true);
        event.target.value = '';
      }
    },
    [appendAttachments, setDraftDirty]
  );

  const removeAttachment = useCallback(
    (index: number) => {
      removeAttachmentFromStore(index);
      setDraftDirty(true);
    },
    [removeAttachmentFromStore, setDraftDirty]
  );

  const forceSaveDraft = useCallback(async (): Promise<boolean> => {
    return persistDraft({ force: true });
  }, [persistDraft]);

  // Cmd+Enter / Ctrl+Enter to send is handled via the editor's handleKeyDown
  // in editorProps (above) so it fires before Tiptap inserts a line break.

  return {
    editor,
    fromAccountId,
    to,
    cc,
    bcc,
    subject,
    showCc,
    showBcc,
    attachments,
    sending,
    focusedField,
    suggestedContacts,
    selectedSignatureId,
    setTo,
    setCc,
    setBcc,
    setSubject,
    setShowCc,
    setShowBcc,
    setFocusedField,
    appendAttachments,
    removeAttachment,
    handleAccountChange,
    handleSend,
    handleScheduleSend,
    attachmentNagOpen: attachmentNag !== null,
    dismissAttachmentNag,
    confirmSendWithoutAttachments,
    handleFileSelect,
    applyContactSuggestion,
    handleSignatureSelect,
    insertInlineImageFromFile,
    draftId,
    draftSaving,
    draftDirty,
    draftError,
    handleDiscardDraft,
    forceSaveDraft,
    hasDraftContent,
    hasUserModified,
    draftLastSavedAt,
    setActiveInputValue,
  };
}
